// Google Analytics 4 + Google Ads conversion tracking, gated on consent
// (Google Consent Mode v2). IDs live in ADMIN → TRACKING (stored server-side)
// so the owner can add them later without rebuilding the site. Until the
// visitor accepts — or the owner pastes IDs — nothing is loaded or sent.

import API from "../api";

const CONSENT_KEY = "cil-analytics-consent";

// Primary and secondary conversions: GA4 event name → the Google Ads
// conversion label it should also fire (label key from tracking settings).
const CONVERSION_EVENTS = {
  generate_lead: "ads_label_enquiry",
  tel_click: "ads_label_phone",
  email_click: "ads_label_email",
};

let settings = null;
let settingsPromise = null;
let scriptsLoaded = false;
const pendingEvents = [];

export const hasConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
};

// Consent Mode v2 — Google signals default to denied BEFORE anything loads.
export const applyConsentDefaults = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    url_passthrough: true,
  });
};
applyConsentDefaults();

const fetchSettings = () => {
  if (!settingsPromise) {
    settingsPromise = fetch(`${API}/api/settings/tracking`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        settings = data || {};
        maybeInjectGscMeta();
        maybeLoadScripts();
        return settings;
      })
      .catch(() => {
        settings = {};
        return settings;
      });
  }
  return settingsPromise;
};

const adsSendTo = (labelKey) => {
  const id = (settings?.ads_conversion_id || "").trim();
  const label = (settings?.[labelKey] || "").trim();
  return id && label ? `${id}/${label}` : null;
};

const gtagIds = () => {
  const ga4 = (settings?.ga4_measurement_id || process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
  const ads = (settings?.ads_conversion_id || "").trim();
  const primary = ga4 || ads;
  return primary ? { primary, ga4, ads } : null;
};

const loadScripts = ({ primary, ga4, ads }) => {
  if (scriptsLoaded) return;
  scriptsLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${primary}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  if (ga4) window.gtag("config", ga4, { anonymize_ip: true });
  if (ads) window.gtag("config", ads);
  while (pendingEvents.length) window.gtag(...pendingEvents.shift());
};

const maybeLoadScripts = () => {
  const ids = gtagIds();
  if (!ids || !hasConsent()) return; // no IDs yet, or visitor declined
  window.gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  loadScripts(ids);
};

// Search Console verification tag, injected once the owner saves the token.
const maybeInjectGscMeta = () => {
  const token = (settings?.gsc_verification_token || "").trim();
  if (!token) return;
  let el = document.querySelector('meta[name="google-site-verification"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "google-site-verification");
    document.head.appendChild(el);
  }
  el.setAttribute("content", token);
};

export const grantConsent = () => {
  try {
    localStorage.setItem(CONSENT_KEY, "granted");
  } catch {}
  initAnalytics();
};

export const revokeConsent = () => {
  try {
    localStorage.setItem(CONSENT_KEY, "denied");
  } catch {}
  // Keep Google's denied-consent signal on record for this visit.
  window.gtag("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
};

export const initAnalytics = () => {
  fetchSettings();
  maybeLoadScripts();
};

export const trackEvent = (name, params = {}) => {
  if (!hasConsent() || typeof window.gtag !== "function") return;
  if (!scriptsLoaded) {
    if (pendingEvents.length < 20) pendingEvents.push(["event", name, params]);
    return;
  }
  window.gtag("event", name, params);
  const labelKey = CONVERSION_EVENTS[name];
  if (labelKey) {
    const sendTo = adsSendTo(labelKey);
    if (sendTo) window.gtag("event", "conversion", { send_to: sendTo, ...params });
  }
};

// Fire-at-most-once conversions (e.g. the payment page can re-render).
export const trackOnce = (key, name, params = {}) => {
  try {
    if (sessionStorage.getItem(`cil-conv-${key}`)) return;
    sessionStorage.setItem(`cil-conv-${key}`, "1");
  } catch {}
  trackEvent(name, params);
};

// One global listener covers every phone, email, WhatsApp, social and CTA
// link — current and future (nav, footer, contact cards, floating buttons).
export const installGlobalTracking = () => {
  if (window.__cilGlobalTracking) return;
  window.__cilGlobalTracking = true;
  document.addEventListener(
    "click",
    (e) => {
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const text = (a.textContent || "").trim().slice(0, 40);
      if (href.startsWith("tel:")) {
        trackEvent("tel_click", { link_text: text });
      } else if (href.startsWith("mailto:")) {
        trackEvent("email_click", { link_text: text });
      } else if (href.includes("wa.me") || href.includes("whatsapp")) {
        trackEvent("whatsapp_click");
      } else if (/facebook\.com|instagram\.com/.test(href)) {
        trackEvent("social_outbound", { network: href.includes("facebook") ? "facebook" : "instagram" });
      } else if (/^\/(contact|availability|build-your-island-weekend|gift-vouchers)/.test(href)) {
        trackEvent("cta_click", { cta_destination: href, link_text: text });
      }
    },
    { passive: true }
  );
};
